import { useQuery } from '@tanstack/react-query'
import { BookOpen, CheckCircle2, Info } from 'lucide-react'
import { useEffect, useState } from 'react'

import { customerPassbooksQueryOptions } from '@/api/savings-api'
import type { LoanPassbook } from '@/api/savings-types'
import { ErrorSurface } from '@/components/error-surface'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { formatShortDate } from '@/lib/datetime'

import { passbookStatusDescription } from './constants'
import { useIssuePassbook } from './mutations'
import { useSavingsPermissions } from './permissions'
import { SavingsActionDialog } from './savings-action-dialog'

const today = () => new Date().toISOString().slice(0, 10)

const NO_REPLACEMENT = 'none'

/**
 * The books issued to a customer.
 *
 * The registry is **branch-scoped on read and institution-wide on write**, and the gap between
 * those two is the whole reason this panel says what it says. The list is filtered to branches the
 * caller can reach; the issue rules are not — a serial is unique across every branch, and the rule
 * that a customer holds one active book at a time is checked with no branch filter at all.
 *
 * So an empty list here is not proof the customer holds no book. Someone who transferred in from
 * another branch may hold an active one this screen cannot display, and issuing a second is refused
 * with `savings.passbook_already_held` for a book the operator cannot see. That is stated up front
 * rather than left to arrive as a puzzling error.
 */
export function PassbooksPanel({
  customerId,
  branchId,
}: {
  customerId: string
  /** The branch the book is physically handed over at, from the header's branch selection. */
  branchId: string | null
}) {
  const permissions = useSavingsPermissions()
  const [issuing, setIssuing] = useState(false)

  const query = useQuery(customerPassbooksQueryOptions(customerId))

  const active = (query.data ?? []).filter((passbook) => passbook.status === 'Active')
  const replaceable = (query.data ?? []).filter(
    (passbook) => passbook.status === 'Active' || passbook.status === 'Lost',
  )

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="min-w-0">
            <CardTitle>Passbooks</CardTitle>
            <p className="mt-0.5 text-xs text-muted">
              Every book issued to this customer at a branch you can see, replaced ones included.
            </p>
          </div>
          {permissions.canOpenAccount && branchId !== null ? (
            <Button variant="secondary" size="sm" onClick={() => setIssuing(true)}>
              <BookOpen />
              Issue a book
            </Button>
          ) : null}
        </CardHeader>
        <CardContent className="space-y-3">
          {query.isPending ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : query.isError ? (
            <ErrorSurface
              error={query.error}
              onRetry={() => void query.refetch()}
              title="The passbook register could not be read"
            />
          ) : query.data.length === 0 ? (
            <Alert tone="info">
              <Info aria-hidden />
              <div className="space-y-1">
                <AlertTitle>No book at a branch you can see</AlertTitle>
                <AlertDescription>
                  This list is filtered to your branch reach. The rules behind issuing are not: a
                  customer holds one active book across the whole institution, so if they were issued
                  one elsewhere, issuing here is refused with{' '}
                  <span className="font-mono text-xs">savings.passbook_already_held</span> for a book
                  this screen cannot show you. Ask the customer before issuing.
                </AlertDescription>
              </div>
            </Alert>
          ) : (
            <ul className="space-y-2">
              {query.data.map((passbook) => (
                <PassbookRow key={passbook.id} passbook={passbook} all={query.data} />
              ))}
            </ul>
          )}

          {active.length > 1 ? (
            <Alert tone="warning">
              <Info aria-hidden />
              <div className="space-y-1">
                <AlertTitle>More than one active book</AlertTitle>
                <AlertDescription>
                  Only one should be active at a time. Retire or replace the others.
                </AlertDescription>
              </div>
            </Alert>
          ) : null}
        </CardContent>
      </Card>

      <IssuePassbookDialog
        customerId={customerId}
        branchId={branchId}
        replaceable={replaceable}
        open={issuing}
        onOpenChange={setIssuing}
      />
    </div>
  )
}

function PassbookRow({ passbook, all }: { passbook: LoanPassbook; all: LoanPassbook[] }) {
  const replaced =
    passbook.replacesPassbookId === null
      ? null
      : all.find((candidate) => candidate.id === passbook.replacesPassbookId)

  return (
    <li className="rounded-card border border-border-subtle bg-surface-muted p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="font-mono text-sm font-medium text-heading">{passbook.passbookNumber}</span>
        <StatusBadge status={passbook.status} />
      </div>
      <p className="mt-1 text-xs text-body">{passbookStatusDescription(passbook.status)}</p>
      <p className="numeric mt-1 text-2xs text-muted tabular-nums">
        Issued {formatShortDate(passbook.issuedDate)}
        {passbook.replacesPassbookId === null
          ? ''
          : replaced
            ? ` · replaced ${replaced.passbookNumber}`
            : // The book it replaced was issued at a branch outside this caller's reach, so the
              // list has the id but not the row. Saying which is honest; showing a bare id is not.
              ' · replaced a book issued outside your branch reach'}
      </p>
    </li>
  )
}

function IssuePassbookDialog({
  customerId,
  branchId,
  replaceable,
  open,
  onOpenChange,
}: {
  customerId: string
  branchId: string | null
  replaceable: LoanPassbook[]
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [passbookNumber, setPassbookNumber] = useState('')
  const [issuedDate, setIssuedDate] = useState(today)
  const [replaces, setReplaces] = useState(NO_REPLACEMENT)
  const [issued, setIssued] = useState<LoanPassbook | null>(null)

  const issue = useIssuePassbook(customerId)

  useEffect(() => {
    if (!open) {
      setPassbookNumber('')
      setIssuedDate(today())
      setReplaces(NO_REPLACEMENT)
      setIssued(null)
      issue.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const trimmed = passbookNumber.trim()

  return (
    <SavingsActionDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Issue a passbook"
      description="Registers a physical book against this customer, or reissues one after a loss."
      notice={
        <Alert tone="info">
          <Info aria-hidden />
          <div className="space-y-1">
            <AlertTitle>Both rules reach across every branch</AlertTitle>
            <AlertDescription>
              The serial has to be unused anywhere in the institution, and the customer must hold no
              active book anywhere in it — neither check is limited to your branch, and the list
              behind this dialog is. A refusal here can be about a book you cannot see.
            </AlertDescription>
          </div>
        </Alert>
      }
      outcome={
        issued ? (
          <Alert tone="success">
            <CheckCircle2 aria-hidden />
            <div className="space-y-1">
              <AlertTitle>Book {issued.passbookNumber} issued</AlertTitle>
              <AlertDescription>
                Issued {formatShortDate(issued.issuedDate)}
                {issued.replacesPassbookId === null
                  ? '. It is now the customer’s active book.'
                  : '. The book it replaces is now marked replaced.'}
              </AlertDescription>
            </div>
          </Alert>
        ) : null
      }
      confirmLabel="Issue book"
      isPending={issue.isPending}
      disabled={trimmed === '' || issuedDate === '' || branchId === null}
      error={issue.isError ? issue.error : null}
      onConfirm={() => {
        if (branchId === null) {
          return
        }

        issue.mutate(
          {
            passbookNumber: trimmed,
            // The branch the book is physically handed over at, taken from the header's selection
            // rather than typed. The same selection also rides on the request as `X-Branch-Id`, and
            // the two disagreeing is not a state this screen can produce.
            branchId,
            issuedDate,
            replacesPassbookId: replaces === NO_REPLACEMENT ? null : replaces,
          },
          { onSuccess: setIssued },
        )
      }}
    >
      <div className="space-y-1.5">
        <Label htmlFor="passbook-serial">Serial</Label>
        <Input
          id="passbook-serial"
          value={passbookNumber}
          autoComplete="off"
          spellCheck={false}
          autoFocus
          onChange={(event) => setPassbookNumber(event.target.value)}
          aria-describedby="passbook-serial-help"
        />
        <p id="passbook-serial-help" className="text-xs text-muted">
          As printed on the cover of the book in your hand.
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="passbook-issued">Issued date</Label>
        <Input
          id="passbook-issued"
          type="date"
          value={issuedDate}
          max={today()}
          onChange={(event) => setIssuedDate(event.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="passbook-replaces">Replaces</Label>
        <Select value={replaces} onValueChange={setReplaces}>
          <SelectTrigger id="passbook-replaces">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={NO_REPLACEMENT}>Nothing — this is a first issue</SelectItem>
            {replaceable.map((candidate) => (
              <SelectItem key={candidate.id} value={candidate.id}>
                {candidate.passbookNumber} ({candidate.status})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted">
          A customer holds one active book. If they already have one, replace it here rather than
          issuing a second.
        </p>
      </div>
    </SavingsActionDialog>
  )
}
